import { STAGE_LABEL, type Stage } from './stage';

/**
 * Taking a case over. Section 7 of the request of 20 September 2026, and section
 * 9 of the request of 5 October 2026. The request of 5 October 2026 (third),
 * section 1, reworded the Final Check refusal and gave it a problem type of its
 * own, so the screen can show it as a message with one OK button and not as an
 * error.
 *
 * A technician scanning a tray from another department used to see "This tray is
 * at Plaster / Model, which is not yours" and nothing else. The first request
 * added the obvious next step: tell them it will be transferred, ask whether
 * they are sure, and let them work it. The second added the case that is
 * ASSIGNED to somebody else, the stages nobody may take from, and the lab's
 * setting for Final Check.
 *
 * So a take is one of three things, and `takeKind` says which:
 *
 *   - `mine`     the case is already this person's. Nothing to take.
 *   - `take`     nobody holds it and it is at their own department. It is simply
 *                taken, with the ordinary confirmation.
 *   - `takeOver` somebody else holds it, or it is at another department. This is
 *                the one with rules: it needs the warning, and it may be refused.
 *
 * Nothing new in the state machine for a case at another department: a take-over
 * is a FLAG to the caller's own department, written by the system, followed by a
 * start, in one command, so a failed start cannot leave a moved case nobody is
 * working. A case at their own department does not move at all; it changes
 * hands.
 *
 * The server enforces every rule here. This file is the same rule, worded the
 * same way, so the screen can say why before anybody presses anything, and the
 * mock server answers as the API does.
 */

/** The note the system writes on the entry it closes. Never typed by anybody. */
export function takeOverNote(input: { byName: string; to: Stage; from: Stage }): string {
  return `Taken by ${input.byName} at ${STAGE_LABEL[input.to]} from ${STAGE_LABEL[input.from]}`;
}

/**
 * A case at Final Check while the lab's setting is off, in the client's two
 * lines. The request of 5 October 2026 (third), section 1. The first is the
 * title of the message the screen shows and the second is what the person can
 * do about it; the detail the server refuses with is the two, a space between.
 */
const FINAL_CHECK_OFF = "Case at Final Check. It can't be taken over.";
const FINAL_CHECK_ASK = 'Ask management to assign this case to you.';

/**
 * The refusals, worded once. The server says them, the mock says them the same
 * way, and the screen renders whichever arrives verbatim.
 */
export const TAKE_OVER_REFUSAL = {
  /**
   * Somebody else has a clock running on it.
   *
   * Pulling a case off a running clock is not a transfer, it is a fight. The
   * message names the person so the two of them can sort it out between
   * themselves, which is faster than either of them asking a manager.
   */
  beingWorked: (name: string) => `${name} is working on this now.`,
  onHold: 'This case is on hold.',
  /**
   * A case that is Ready or in Logistics. The client's words, with who may
   * assign it since the lab's request of 18 October 2026: anybody who can
   * assign work, not only an admin.
   */
  notAtThisStage:
    "This case can't be taken over at this stage. It can only be assigned by someone who can assign work.",
  /** A case at Final Check while the lab's setting is off: the first line, the message's title. */
  finalCheckOff: FINAL_CHECK_OFF,
  /** The second line: what the person can do about it. */
  finalCheckAsk: FINAL_CHECK_ASK,
  /** The two lines as one: the detail the refusal carries, whoever asked and however. */
  finalCheckOffDetail: `${FINAL_CHECK_OFF} ${FINAL_CHECK_ASK}`,
} as const;

/**
 * The problem types a refused take-over answers with, where it has one of its
 * own. The server's TakeOverRules.FinalCheckOffType. Every other refusal of a
 * take-over is the ordinary conflict.
 *
 * A problem type is the name the server gives a refusal, in the form RFC 7807
 * asks for. Nothing is ever fetched from it, so it is not the address the lint
 * rule below is there to keep out of the domain.
 */
export const TAKE_OVER_PROBLEM_TYPE = {
  // eslint-disable-next-line no-restricted-syntax -- a name to compare with, not an address to call
  finalCheckOff: 'https://ceramica.app/errors/take-over-final-check',
} as const;

/**
 * Whether a rejected start is the Final Check refusal: the server would not let
 * this person take a case at Final Check while the setting is off.
 *
 * Structural, as `httpStatusOf` is, because the domain cannot import the class
 * the http client throws. Anything that is not that one problem type, a dropped
 * connection and a refusal whose body could not be read included, is not it.
 */
export function isFinalCheckRefusal(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('problem' in error)) return false;
  const { problem } = error;
  if (typeof problem !== 'object' || problem === null || !('type' in problem)) return false;
  return problem.type === TAKE_OVER_PROBLEM_TYPE.finalCheckOff;
}

export type TakeKind = 'mine' | 'take' | 'takeOver';

/**
 * What taking this case would be, for this person.
 *
 * `worksItsDepartment` is whether they work the department the case sits at:
 * their own, or any of them for management.
 */
export function takeKind(input: {
  worksItsDepartment: boolean;
  assignedToUserId: string | null;
  userId: string;
}): TakeKind {
  if (input.assignedToUserId === input.userId) return 'mine';
  if (input.worksItsDepartment && input.assignedToUserId === null) return 'take';
  return 'takeOver';
}

/**
 * Why a case at this stage cannot be taken over, or null when its stage allows
 * it. Ready is never taken from, and a case in Logistics is at Ready. Final
 * Check is taken from only while management has switched the setting on.
 */
export function takeOverStageRefusal(stage: Stage, allowedAtFinalCheck: boolean): string | null {
  if (stage === 'ready') return TAKE_OVER_REFUSAL.notAtThisStage;
  if (stage === 'finalcheck' && !allowedAtFinalCheck) return TAKE_OVER_REFUSAL.finalCheckOffDetail;
  return null;
}

/** Whether a case at this stage is closed to a take-over. */
export function takeOverClosed(stage: Stage, allowedAtFinalCheck: boolean): boolean {
  return takeOverStageRefusal(stage, allowedAtFinalCheck) !== null;
}

/**
 * Whether Final Check is closed to this person: the case is at Final Check,
 * taking it would be a take-over (it is assigned to somebody else, or they do
 * not work Final Check), and the lab's setting is off. The request of
 * 5 October 2026 (third), section 1.
 *
 * One rule for the three places that read it: the screen when a scan arrives,
 * the buttons that would send the person to the scanner, and the mock server,
 * which answers with the problem type above exactly when this is true. The
 * person the case is assigned to, and somebody who works Final Check starting
 * a case nobody holds, are not taking anything over, and this is false for
 * them.
 */
export function finalCheckClosedTo(input: {
  stage: Stage;
  kind: TakeKind;
  allowedAtFinalCheck: boolean;
}): boolean {
  return input.stage === 'finalcheck' && input.kind === 'takeOver' && !input.allowedAtFinalCheck;
}

/**
 * Who holds the case and who gave it to them: the first half of the warning, and
 * the refusal when a held case is taken without the confirmation. The second
 * name is left out when nobody recorded it, or when the holder took the case
 * themselves.
 */
export function assignedToSentence(assigneeName: string, assignerName: string | null): string {
  return assignerName === null || assignerName === '' || assignerName === assigneeName
    ? `This case is assigned to ${assigneeName}.`
    : `This case is assigned to ${assigneeName} by ${assignerName}.`;
}

/** The warning's question, after the sentence above. */
export const TAKE_OVER_QUESTION = 'Are you sure you want to take it?';

/** The Lab Notes sentence for a case taken from the person holding it, where it sits. */
export function takenFromSentence(fromName: string, byName: string): string {
  return `Taken over from ${fromName} by ${byName}`;
}

/** What the person it was taken from is told. */
export function toldTakenSentence(jobOrderNo: string, byName: string): string {
  return `${jobOrderNo} was taken over by ${byName}`;
}
